import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Helper function to get unique localStorage key based on response ID
 */
const getStorageKey = (responseId?: string): string => {
    const suffix = responseId ? `_${responseId}` : '';
    return `ai_override_preference${suffix}`;
};

/**
 * Clears the stored AI override preference for a survey response.
 */
export const clearStoredOverridePreference = (responseId?: string): void => {
    localStorage.removeItem(getStorageKey(responseId));
};

/**
 * Custom hook for managing AI override preference persistence in localStorage
 * 
 * @param responseId - Optional response ID for scoped storage
 * @returns Object with isDataOverriden state, setIsDataOverriden setter, and clearPreference method
 */
export const useOverrideStorage = (responseId: string | undefined) => {
    const [isDataOverriden, setIsDataOverridenState] = useState<boolean>(false);
    const hasLoadedFromStorage = useRef(false);

    /**
     * Loads preference from localStorage on mount
     */
    useEffect(() => {
        // Only load once on mount
        if (hasLoadedFromStorage.current) {
            return;
        }

        const storageKey = getStorageKey(responseId);
        const storedValue = localStorage.getItem(storageKey);

        if (storedValue !== null) {
            try {
                const preference = JSON.parse(storedValue) as boolean;
                setIsDataOverridenState(preference);
            } catch (error) {
                console.error('Failed to load override preference from localStorage:', error);
            }
        }

        hasLoadedFromStorage.current = true;
    }, [responseId]);

    /**
     * Saves preference to localStorage whenever it changes
     */
    useEffect(() => {
        // Don't save until we've loaded from storage first
        if (!hasLoadedFromStorage.current) {
            return;
        }

        const storageKey = getStorageKey(responseId);
        localStorage.setItem(storageKey, JSON.stringify(isDataOverriden));
    }, [isDataOverriden, responseId]);

    /**
     * Sets the override preference and persists to localStorage
     */
    const setIsDataOverriden = useCallback((value: boolean) => {
        setIsDataOverridenState(value);
    }, []);

    /**
     * Clears preference from localStorage
     */
    const clearPreference = useCallback(() => {
        clearStoredOverridePreference(responseId);
        setIsDataOverridenState(false);
    }, [responseId]);

    return {
        isDataOverriden,
        setIsDataOverriden,
        clearPreference
    };
};
